import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, CheckCircle, ArrowDownLeft, ArrowUpRight, Clock, Coins } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatDate, daysUntil } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { type: "receivable", amount: "", currency: "TRY", contact_id: "", contact_name: "", account_id: "", due_date: "", description: "" };
const NONE = "__none__";

export default function Payments() {
  const { currencies } = useCurrency();
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(() => {
    api.get("/payments").then((r) => setItems(r.data)).catch(() => {});
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickContact = (v) => {
    if (v === NONE) return setForm((f) => ({ ...f, contact_id: "", contact_name: "" }));
    const c = contacts.find((x) => x.id === v);
    setForm((f) => ({ ...f, contact_id: v, contact_name: c ? c.name : "" }));
  };
  const openNew = () => { setForm(empty); setOpen(true); };

  const submit = async () => {
    if (!form.amount || !form.due_date) return toast.error("Tutar ve vade tarihi girin");
    try {
      await api.post("/payments", {
        ...form, amount: Number(form.amount), contact_id: form.contact_id || null, account_id: form.account_id || null, due_date: new Date(form.due_date).toISOString(),
      });
      toast.success("Kayıt eklendi");
      setOpen(false);
      load();
    } catch (e) { toast.error("Eklenemedi"); }
  };

  const settle = async (id) => { await api.post(`/payments/${id}/settle`); toast.success("Kapatıldı ve işleme dönüştürüldü"); load(); };
  const remove = async (id) => { await api.delete(`/payments/${id}`); toast.success("Silindi"); load(); };

  const [partialOpen, setPartialOpen] = useState(false);
  const [partialTarget, setPartialTarget] = useState(null);
  const [partialForm, setPartialForm] = useState({ amount: "", account_id: "" });
  const openPartial = (p) => { setPartialTarget(p); setPartialForm({ amount: "", account_id: p.account_id || "" }); setPartialOpen(true); };
  const submitPartial = async () => {
    if (!partialForm.amount || Number(partialForm.amount) <= 0) return toast.error("Geçerli tutar girin");
    try {
      await api.post(`/payments/${partialTarget.id}/pay-partial`, { amount: Number(partialForm.amount), account_id: partialForm.account_id || null });
      toast.success("Kısmi ödeme kaydedildi");
      setPartialOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlenemedi"); }
  };

  const filtered = items.filter((p) => filter === "all" || p.status === filter);
  const receivables = filtered.filter((p) => p.type === "receivable");
  const payables = filtered.filter((p) => p.type === "payable");

  const Row = ({ p }) => {
    const d = daysUntil(p.due_date);
    const overdue = d < 0 && p.status === "pending";
    const paid = p.paid_amount || 0;
    const remaining = Math.max(p.amount - paid, 0);
    const pct = p.amount > 0 ? Math.min(100, Math.round((paid / p.amount) * 100)) : 0;
    return (
      <div data-testid={`payment-row-${p.id}`} className="flex items-center gap-4 border border-border rounded-md p-4 bg-secondary/20 transition-colors hover:bg-secondary/40">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{p.contact_name || p.description || "—"}</div>
          <div className="text-xs text-muted-foreground truncate">{p.description}</div>
          <div className="mt-1.5 flex items-center gap-2">
            <Clock size={13} className={overdue ? "text-destructive" : "text-muted-foreground"} />
            <span className={`text-xs ${overdue ? "text-destructive" : "text-muted-foreground"}`}>
              {formatDate(p.due_date)}
              {p.status === "pending" && (overdue ? ` · ${Math.abs(d)} gün gecikti` : ` · ${d} gün kaldı`)}
            </span>
            {p.status === "paid" && <Badge variant="outline" className="border-[#10B981]/40 text-[#10B981] text-[10px]">Kapatıldı</Badge>}
            {p.created_by_name && (
              <Badge variant="outline" data-testid={`payment-enteredby-${p.id}`}
                className={p.created_by_role === "staff" ? "border-primary/40 text-primary text-[10px]" : "border-border text-muted-foreground text-[10px]"}>
                {p.created_by_role === "staff" ? "👤 " : ""}{p.created_by_name}
              </Badge>
            )}
          </div>
          {paid > 0 && p.status === "pending" && (
            <div className="mt-2" data-testid={`payment-progress-${p.id}`}>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                Ödendi: {formatMoney(paid, p.currency, currencies)} · Kalan: <span className="text-foreground font-medium">{formatMoney(remaining, p.currency, currencies)}</span>
              </div>
            </div>
          )}
        </div>
        <div className={`font-mono-num font-semibold text-right ${p.type === "receivable" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
          {formatMoney(p.amount, p.currency, currencies)}
        </div>
        <div className="flex gap-1">
          {p.status === "pending" && (
            <button onClick={() => openPartial(p)} data-testid={`payment-partial-${p.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title="Kısmi Öde">
              <Coins size={18} />
            </button>
          )}
          {p.status === "pending" && (
            <button onClick={() => settle(p.id)} data-testid={`payment-settle-${p.id}`} className="text-muted-foreground hover:text-[#10B981] transition-colors p-1" title="Tümünü Kapat">
              <CheckCircle size={18} />
            </button>
          )}
          <ConfirmDelete onConfirm={() => remove(p.id)} testId={`payment-delete-${p.id}`}
            title="Kaydı sil?" description="Bu ödeme/tahsilat kaydı silinecek. Bu işlem geri alınamaz.">
            <Trash size={16} />
          </ConfirmDelete>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Ödeme & Tahsilat</h1>
          <p className="text-sm text-muted-foreground mt-1">Yaklaşan ödemelerinizi ve tahsilatlarınızı planlayın</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-payment-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus size={18} className="mr-1" /> Yeni Kayıt
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">Yeni Ödeme / Tahsilat</DialogTitle>
              <DialogDescription className="text-muted-foreground">Yaklaşan bir ödeme veya tahsilat kaydı oluşturun.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={form.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="pay-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="receivable">Tahsilat (Alacak)</SelectItem>
                      <SelectItem value="payable">Ödeme (Borç)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Vade Tarihi</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="pay-due" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tutar</Label>
                  <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="pay-amount" placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="pay-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Cari / Taraf</Label>
                <Select value={form.contact_id || NONE} onValueChange={pickContact}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="pay-contact"><SelectValue placeholder="Cari seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Cari yok —</SelectItem>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Banka Hesabı</Label>
                <Select value={form.account_id || NONE} onValueChange={(v) => set("account_id", v === NONE ? "" : v)}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="pay-account"><SelectValue placeholder="Hesap seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Hesap yok —</SelectItem>
                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Kayıt kapatıldığında tutar bu hesaba işlenir</p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Açıklama</Label>
                <Input value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="pay-description" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="pay-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2">
        {[["pending", "Bekleyen"], ["paid", "Kapatılan"], ["all", "Tümü"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`payment-filter-${v}`}
            className={`px-4 py-1.5 rounded-md text-sm transition-colors ${filter === v ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <ArrowDownLeft size={20} className="text-[#10B981]" />
            <h2 className="font-heading text-lg">Tahsilatlar</h2>
          </div>
          <div className="space-y-3">
            {receivables.length === 0 ? <p className="text-sm text-muted-foreground">Kayıt yok.</p> : receivables.map((p) => <Row key={p.id} p={p} />)}
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <ArrowUpRight size={20} className="text-[#EF4444]" />
            <h2 className="font-heading text-lg">Ödemeler</h2>
          </div>
          <div className="space-y-3">
            {payables.length === 0 ? <p className="text-sm text-muted-foreground">Kayıt yok.</p> : payables.map((p) => <Row key={p.id} p={p} />)}
          </div>
        </div>
      </div>

      <Dialog open={partialOpen} onOpenChange={setPartialOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Kısmi Ödeme / Tahsilat</DialogTitle>
            <DialogDescription className="text-muted-foreground">Kaydın bir kısmını ödeyin; kalan tutar takip edilir.</DialogDescription>
          </DialogHeader>
          {partialTarget && (
            <div className="space-y-4">
              <div className="border border-border rounded-md px-4 py-3 bg-secondary/30 text-sm">
                <div className="font-medium">{partialTarget.contact_name || partialTarget.description || "—"}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Toplam: {formatMoney(partialTarget.amount, partialTarget.currency, currencies)} · Kalan: <span className="text-foreground font-medium">{formatMoney(Math.max(partialTarget.amount - (partialTarget.paid_amount || 0), 0), partialTarget.currency, currencies)}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Bu Sefer Ödenecek Tutar ({partialTarget.currency})</Label>
                <Input type="number" value={partialForm.amount} onChange={(e) => setPartialForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1.5 bg-secondary border-border" data-testid="partial-amount" placeholder="0" />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Banka Hesabı</Label>
                <Select value={partialForm.account_id || NONE} onValueChange={(v) => setPartialForm((f) => ({ ...f, account_id: v === NONE ? "" : v }))}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="partial-account"><SelectValue placeholder="Hesap seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Hesap yok —</SelectItem>
                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={submitPartial} data-testid="partial-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Coins size={16} className="mr-1.5" /> Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
